
import React, { useState } from 'react';
import type { ExternalEvent } from '../types';
import { XIcon, PlusIcon } from './icons/PlaybackIcons';
import { eventIntegrationService } from '../services/eventIntegrationService';

interface EventSearchModalProps {
    onClose: () => void;
    onAddEvent: (event: ExternalEvent) => Promise<void>;
    existingEventIds: Set<string>;
}

const EventSearchModal: React.FC<EventSearchModalProps> = ({ onClose, onAddEvent, existingEventIds }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState<ExternalEvent[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [addingId, setAddingId] = useState<string | null>(null);

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchTerm.trim()) return;

        setIsLoading(true);
        setError(null);
        try {
            const data = await eventIntegrationService.searchEvents(searchTerm);
            setResults(data);
            if (data.length === 0) setError("No events found matching your search.");
        } catch (err) {
            setError("Failed to search events. Please check your connection.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddClick = async (event: ExternalEvent) => {
        setAddingId(event.id);
        try {
            await onAddEvent(event);
        } catch (e) {
            // Error handled in parent or ignored
        } finally {
            setAddingId(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl border border-gray-700 flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-cyan-400">Find Upcoming Events</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="p-4 bg-gray-800/50">
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search for a race name or city..."
                            className="flex-grow bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                        <button 
                            type="submit" 
                            disabled={isLoading}
                            className="px-6 py-2 bg-cyan-600 text-white font-bold rounded-lg hover:bg-cyan-500 transition-colors disabled:opacity-50"
                        >
                            {isLoading ? 'Searching...' : 'Search'}
                        </button>
                    </form>
                    {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
                </div>

                {/* Results List */}
                <div className="flex-grow overflow-y-auto p-4 space-y-3">
                    {results.length > 0 ? (
                        results.map(event => {
                            const isAdded = existingEventIds.has(event.id);
                            const isProcessing = addingId === event.id;

                            return (
                                <div key={event.id} className="bg-gray-700 p-4 rounded-lg flex justify-between items-center border border-gray-600">
                                    <div>
                                        <h3 className="font-bold text-white text-lg">{event.name}</h3>
                                        <div className="text-gray-400 text-sm flex gap-4 mt-1">
                                            <span>{new Date(event.date).toLocaleDateString()}</span>
                                            <span>•</span>
                                            <span>{event.location}</span>
                                        </div>
                                        {event.websiteUrl && (
                                            <a href={event.websiteUrl} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:underline mt-1 inline-block">
                                                Event Website
                                            </a>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleAddClick(event)}
                                        disabled={isAdded || isProcessing}
                                        className={`px-4 py-2 rounded-md font-medium text-sm transition-colors flex items-center gap-2 ${
                                            isAdded 
                                            ? 'bg-green-900/50 text-green-400 border border-green-800 cursor-default' 
                                            : 'bg-cyan-600 text-white hover:bg-cyan-500'
                                        }`}
                                    >
                                        {isProcessing ? 'Adding...' : isAdded ? 'Added' : (
                                            <>
                                                <PlusIcon className="w-4 h-4" />
                                                Add to Calendar
                                            </>
                                        )}
                                    </button>
                                </div>
                            );
                        })
                    ) : (
                        !isLoading && (
                            <div className="text-center text-gray-500 py-10">
                                Search for an event to add it to your profile.
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventSearchModal;
